import { assertPreviewResponse } from "./lib/previewResponse";

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
const DEFAULT_PRODUCTION_API_BASE = "https://api-to-mcp.onrender.com";
const RENDER_MCP_HOSTNAME = new URL(DEFAULT_PRODUCTION_API_BASE).hostname;

/** Backend origin for browser `fetch` calls. Prefer `VITE_API_URL`; otherwise same origin as the SPA (Vite proxies `/api` → FastAPI). */
function isProductionSiteHost(hostname: string): boolean {
  return hostname === "apitomcpconverter.com" || hostname === "www.apitomcpconverter.com";
}

function isLocalDevHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function preferredMcpDisplayOrigin(): string | null {
  if (typeof window === "undefined" || !window.location?.origin) return null;
  const { hostname, origin } = window.location;
  if (isProductionSiteHost(hostname) || isLocalDevHost(hostname)) return origin;
  return null;
}

function rewriteLegacyMcpHost(absoluteUrl: string): string {
  try {
    const parsed = new URL(absoluteUrl);
    if (parsed.hostname !== RENDER_MCP_HOSTNAME) return absoluteUrl;
    const displayOrigin = preferredMcpDisplayOrigin();
    if (!displayOrigin) return absoluteUrl;
    return `${displayOrigin.replace(/\/+$/, "")}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return absoluteUrl;
  }
}

export function getApiBase(): string {
  const onProductionSite =
    typeof window !== "undefined" && isProductionSiteHost(window.location.hostname);
  const fromEnv = (viteEnv?.VITE_API_URL as string | undefined)?.trim().replace(/\/+$/, "");
  // Same defense as getConverterApiBase — ignore localhost VITE_API_URL on
  // the production site so a leaked .env.local doesn't break the live app.
  if (onProductionSite) {
    if (fromEnv && !isLocalUrl(fromEnv)) return fromEnv;
    return window.location.origin;
  }
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://127.0.0.1:8000";
}

function isLocalUrl(value: string | undefined): boolean {
  if (!value) return false;
  return /(^|\/\/)(localhost|127\.0\.0\.1)([:/]|$)/.test(value);
}

export function getConverterApiBase(): string {
  const onProductionSite =
    typeof window !== "undefined" && isProductionSiteHost(window.location.hostname);
  const fromEnv = (viteEnv?.VITE_API_URL as string | undefined)?.trim().replace(/\/+$/, "");
  // On the production site, ignore any localhost VITE_API_URL that might have
  // leaked in from a .env.local during a laptop build. Use same-origin so
  // /api/v1/... is hit on apitomcpconverter.com and Vercel's rewrite proxies
  // to Render — avoiding cross-origin CORS errors entirely.
  if (onProductionSite) {
    if (fromEnv && !isLocalUrl(fromEnv)) return fromEnv;
    return window.location.origin;
  }
  if (fromEnv) return fromEnv;
  return getApiBase();
}

/**
 * Base URL for **copy-paste MCP / SSE links** (Cursor, Claude, opening `/mcp/...` in a browser).
 * On the public site and local dev, uses the SPA origin so links stay on the branded host.
 * Set `VITE_MCP_PUBLIC_URL` to override; else `VITE_API_URL`; else `http://127.0.0.1:8000`.
 */
export function getMcpPublicBase(): string {
  const override = (viteEnv?.VITE_MCP_PUBLIC_URL as string | undefined)?.trim().replace(/\/+$/, "");
  if (override) return override;

  const displayOrigin = preferredMcpDisplayOrigin();
  if (displayOrigin) return displayOrigin;

  const fromApi = (viteEnv?.VITE_API_URL as string | undefined)?.trim().replace(/\/+$/, "");
  if (fromApi) return fromApi;

  return "http://127.0.0.1:8000";
}

type ErrorBody =
  | { detail?: unknown; error?: unknown; message?: unknown }
  | string
  | null;

/** Hide raw upstream HTTP codes in URL-fetch errors so users don't blame this app. */
function softenRemoteSpecFetchMessage(msg: string): string {
  const m = msg.trim();
  if (/^No access to this API specification URL/.test(m)) return m;
  const looksLikeUrlFetch =
    /fetching https?:\/\//i.test(m) ||
    /network error fetching/i.test(m) ||
    (/^HTTP \d{3}\b/i.test(m) && /fetching /i.test(m));
  if (looksLikeUrlFetch) {
    return (
      "No access to this API specification URL. The remote host blocked or restricted the download—this is not a problem with this website. " +
      "Try a public spec link, add authorization if required, or upload the file instead."
    );
  }
  return m;
}

// ── helper: extract error message from any failed response ────────────────
const extractError = async (res: Response, fallback: string): Promise<string> => {
  try {
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      const text = (await res.text()).trim();
      return softenRemoteSpecFetchMessage(text || fallback);
    }

    const body = (await res.json()) as ErrorBody;
    if (body && typeof body === "object" && "detail" in body && body.detail) {
      const detail = body.detail as unknown;
      const out = Array.isArray(detail)
        ? detail.map((d) => {
            if (d && typeof d === "object" && "msg" in d) return String((d as { msg?: unknown }).msg ?? JSON.stringify(d));
            return JSON.stringify(d);
          }).join(", ")
        : String(detail);
      return softenRemoteSpecFetchMessage(out);
    }
    if (body && typeof body === "object" && "error" in body && body.error)
      return softenRemoteSpecFetchMessage(String(body.error));
    if (body && typeof body === "object" && "message" in body && body.message)
      return softenRemoteSpecFetchMessage(String(body.message));
    if (typeof body === "string" && body.trim()) return softenRemoteSpecFetchMessage(body.trim());
  } catch {
    // response was not JSON — use fallback
  }
  return softenRemoteSpecFetchMessage(fallback);
};

// ── 1. Parse spec → structured preview data ───────────────────────────────
//    Used by index.tsx for file upload preview
export const uploadOpenAPI = async (file: File) => {
  if (!file) throw new Error("No file selected");

  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${getConverterApiBase()}/api/v1/preview`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `Upload failed (${res.status})`));
  }

  return assertPreviewResponse(await res.json());
};

// ── 2. Convert spec → download MCP server ZIP ─────────────────────────────
//    Used by ConvertAction / preview page generate button
export const convertToMCP = async (file: File) => {
  if (!file) throw new Error("No file selected");

  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${getConverterApiBase()}/api/v1/convert`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP conversion failed (${res.status})`));
  }

  const data = await res.json();
  return data;
};

export const convertToMCPFromUrl = async (url: string, options?: { authorization?: string }) => {
  const u = url.replace(/^\uFEFF/, "").trim();
  if (!u) throw new Error("No URL provided");

  const formData = new FormData();
  formData.append("url", u);
  const auth = options?.authorization?.trim();
  if (auth) formData.append("url_authorization", auth);

  const res = await fetch(`${getConverterApiBase()}/api/v1/convert`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP conversion failed (${res.status})`));
  }

  const data = await res.json();
  return data;
};

// ── 3. Preview from URL (optional — for the URL input tab) ────────────────
export const uploadOpenAPIFromUrl = async (url: string, options?: { authorization?: string }) => {
  const u = url.replace(/^\uFEFF/, "").trim();
  if (!u) throw new Error("No URL provided");

  const formData = new FormData();
  formData.append("url", u);
  const auth = options?.authorization?.trim();
  if (auth) formData.append("url_authorization", auth);

  const res = await fetch(`${getConverterApiBase()}/api/v1/preview`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `Fetch failed (${res.status})`));
  }

  return assertPreviewResponse(await res.json());
};

// ── 4. Preview / convert from pasted JSON or YAML text ─────────────────
export const uploadOpenAPIFromText = async (text: string) => {
  const t = text.replace(/^\uFEFF/, "").trim();
  if (!t) throw new Error("No specification text provided");

  const formData = new FormData();
  formData.append("text", t);

  const res = await fetch(`${getConverterApiBase()}/api/v1/preview`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `Parse failed (${res.status})`));
  }

  return assertPreviewResponse(await res.json());
};

export const convertToMCPFromText = async (text: string) => {
  const t = text.replace(/^\uFEFF/, "").trim();
  if (!t) throw new Error("No specification text provided");

  const formData = new FormData();
  formData.append("text", t);

  const res = await fetch(`${getConverterApiBase()}/api/v1/convert`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP conversion failed (${res.status})`));
  }

  const data = await res.json();
  return data;
};

// ── 5. Host MCP server remotely on Render ─────────────────────────────────
//    Returns { ok, mcp_url, sse_url, server_id, spec, tools }
type HostOptions = {
  email?: string | null;
  credentials?: Record<string, string> | null;
  baseUrlOverride?: string | null;
};

function _appendHostExtras(formData: FormData, options: HostOptions) {
  if (options.email) formData.append("email", options.email);
  if (options.credentials && Object.keys(options.credentials).length > 0) {
    formData.append("credentials_json", JSON.stringify(options.credentials));
  }
  if (options.baseUrlOverride?.trim()) {
    formData.append("base_url_override", options.baseUrlOverride.trim());
  }
}

export const hostMCP = async (file: File, email?: string | null, options?: HostOptions) => {
  if (!file) throw new Error("No file selected");

  const formData = new FormData();
  formData.append("file", file);
  _appendHostExtras(formData, { email, ...options });

  const res = await fetch(`${getConverterApiBase()}/api/v1/host`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP hosting failed (${res.status})`));
  }
  return res.json();
};

export const hostMCPFromUrl = async (url: string, options?: { authorization?: string; email?: string | null } & HostOptions) => {
  const u = url.replace(/^\uFEFF/, "").trim();
  if (!u) throw new Error("No URL provided");

  const formData = new FormData();
  formData.append("url", u);
  const auth = options?.authorization?.trim();
  if (auth) formData.append("url_authorization", auth);
  _appendHostExtras(formData, options ?? {});

  const res = await fetch(`${getConverterApiBase()}/api/v1/host`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP hosting failed (${res.status})`));
  }
  return res.json();
};

export const hostMCPFromText = async (text: string, email?: string | null, options?: HostOptions) => {
  const t = text.replace(/^\uFEFF/, "").trim();
  if (!t) throw new Error("No specification text provided");

  const formData = new FormData();
  formData.append("text", t);
  _appendHostExtras(formData, { email, ...options });

  const res = await fetch(`${getConverterApiBase()}/api/v1/host`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `MCP hosting failed (${res.status})`));
  }
  return res.json();
};

/** Browser link for a hosted MCP path — uses the converter API host in production. */
export function resolveHostedMcpBrowserLink(urlOrPath: string): string {
  return resolveHostedEndpoint(urlOrPath);
}

/** Turn backend-relative paths (`/mcp/...`) into absolute URLs for MCP clients (see `getMcpPublicBase`). */
export function resolveHostedEndpoint(urlOrPath: string): string {
  const u = urlOrPath.trim();
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return rewriteLegacyMcpHost(u);
  const base = getMcpPublicBase().replace(/\/+$/, "");
  const path = u.startsWith("/") ? u : `/${u}`;
  return `${base}${path}`;
}

// ── 6. Merge multiple spec URLs into one merged spec JSON string ──────────
export const mergeSpecsFromUrls = async (urls: string[]): Promise<string> => {
  const clean = urls.map(u => u.replace(/^﻿/, "").trim()).filter(Boolean);
  if (clean.length < 2) throw new Error("Provide at least 2 URLs to merge.");

  const formData = new FormData();
  for (const u of clean) formData.append("urls", u);

  const res = await fetch(`${getConverterApiBase()}/api/v1/merge`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await extractError(res, `Merge failed (${res.status})`));
  }

  const data = await res.json() as { merged_spec?: string };
  if (!data.merged_spec) throw new Error("Merge returned empty spec.");
  return data.merged_spec;
};

// ── decode base64 ZIP and trigger browser download ────────────────────────
export const downloadZipFromBase64 = (zip_b64: string, title: string) => {
  const binary = atob(zip_b64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const blob     = new Blob([bytes], { type: "application/zip" });
  const url      = URL.createObjectURL(blob);
  const anchor   = document.createElement("a");
  anchor.href    = url;
  anchor.download = `${title.toLowerCase().replace(/\s+/g, "-")}-mcp-server.zip`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};